import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import { initializeApp } from "firebase/app";
import { getMessaging, onMessage, getToken, isSupported } from "firebase/messaging";
/**
 * Configurazione dell'applicazione Firebase.
 * Sostituisci i valori con quelli forniti dalla tua console Firebase.
 */
import firebaseConfig from "./firebaseConfig.json";
import { DthHttpClient } from "../clientHttp/DthHttpClient";
import { DthSessionStorage } from "../clientHttp/DthSessionStorage";
import { IMatrixClientCreds, MatrixClientPeg } from "../../MatrixClientPeg";
import * as Lifecycle from "../../Lifecycle";
import { CallLeaveReason, CallRejectReason, CallStatus, useCallStore } from "../../stores/call/useCallStore";
import { _t } from "../../languageHandler";
import { findRoomByDisplayName } from "../utils";
import { PLATFORM_UPPERCASE } from "../clientHttp/utils/utils";

const logger = baseLogger.getChild("Firebase");

const firebaseConfigFiltered = {
    apiKey: firebaseConfig.apiKey,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    storageBucket: firebaseConfig.storageBucket,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
    measurementId: firebaseConfig.measurementId,
    databaseURL: firebaseConfig.databaseURL
};
/**
 * Inizializza l'app Firebase e il servizio di messaggistica.
 */
const app = initializeApp(firebaseConfigFiltered);

/**
 * Istanza di Firebase Cloud Messaging (FCM) per la ricezione delle notifiche push.
 */
export const messaging = getMessaging(app);

/**
 * Si mette in ascolto dei messaggi Firebase quando l'app è in foreground, 
 * utilizzando una callback di default per la gestione delle notifiche relative alle chiamate VoIP.
 */
listenForFirebaseMessagesForCall()

// Ricezione notifiche inoltrate dal service worker Firebase
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', function (event) {
        if (event.data && event.data.type === 'FIREBASE_PUSH') {
            // Gestisci la notifica come in foreground
            defaultCallbackFirebaseMessages(event.data.payload);
        }
    });
}

/**
 * Richiede il permesso all'utente per ricevere notifiche push e restituisce il token di registrazione.
 *
 * @returns {Promise<string | null>} Il token di registrazione FCM, oppure null se non disponibile o negato.
 *
 * @example
 * const token = await requestFirebaseNotificationPermission();
 * if (token) {
 *   // Invia il token al server per associare l'utente
 * }
 */
export async function requestFirebaseNotificationPermission(): Promise<string | null> {
    try {
        // Controllo se è supportato ricevere notifiche push su questo browser
        if (!(await isSupported())) {
            logger.warn("Notifiche push non supportate in questo browser.");
            return null;
        }

        // Richiede il permesso all'utente per ricevere notifiche push
        const permission = await Notification.requestPermission();

        // Se il permesso non è stato concesso, logga un avviso e restituisci null
        if (permission !== "granted") {
            logger.warn("Permesso notifiche non concesso:", permission);
            return null;
        }

        // Alcuni browser (Edge, Firefox) funzionano meglio se si passa
        // esplicitamente la `ServiceWorkerRegistration` a `getToken`.
        let swRegistration: ServiceWorkerRegistration | undefined;
        if ('serviceWorker' in navigator) {
            try {
                // Prova a recuperare la registration specifica del firebase SW,
                // altrimenti attendi che il service worker sia `ready`.
                swRegistration = (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')) || await navigator.serviceWorker.ready;
            } catch (e) {
                logger.debug("ServiceWorker registration non disponibile prima di getToken:", e);
            }
        }

        // Ottiene il token di registrazione FCM, passando la registration se disponibile
        const getTokenOptions: { vapidKey?: string; serviceWorkerRegistration?: ServiceWorkerRegistration } = {
            vapidKey: firebaseConfig.vapidKey
        };
        if (swRegistration) getTokenOptions.serviceWorkerRegistration = swRegistration;

        // Se otteniamo un token, invialo al server per associarlo all'utente
        const currentToken = await getToken(messaging, getTokenOptions);
        if (currentToken) {
            // Invia il token al server se necessario
            try {
                // Recupera le informazioni dell'utente attualmente autenticato
                const currentUserInfo: IMatrixClientCreds = await DthSessionStorage.getCredentials();

                // Se le informazioni dell'utente non sono disponibili o non contengono un deviceId, logga un avviso e restituisci il token senza inviarlo al server
                if (!currentUserInfo || !currentUserInfo.deviceId) {
                    logger.warn("Impossibile registrare il token Firebase: informazioni utente non disponibili.");
                    return currentToken;
                }

                // Invia il token al server per associarlo all'utente, specificando il client e la lingua preferita
                const dthApiClient = DthHttpClient.getInstance();
                dthApiClient.registerFirebaseToken({
                    userId: "me",
                    client: PLATFORM_UPPERCASE,
                    displayName: "Web Browser",
                    pushKey: currentToken,
                    voipKey: undefined,
                    deviceId: currentUserInfo.deviceId,
                    lang: navigator.language?.includes("it") ? "IT" : "EN"
                }).catch((error) => {
                    // Gestione non bloccante: logga l'errore ma non interrompe il flusso
                    logger.error("Errore durante la registrazione del token Firebase sul server:", error);
                });
            } catch (e) {
                logger.error("Errore durante l'invio del token al server:", e);
            }

            return currentToken;
        } else {
            logger.warn("Nessun token di registrazione disponibile. Richiedi permesso all'utente.");
            return null;
        }
    } catch (err) {
        logger.error("Errore durante la richiesta del token di notifica Firebase:", err);
        return null;
    }
}

/**
 * Registra un callback per ricevere notifiche push quando l'app è in foreground.
 */
export function listenForFirebaseMessagesForCall(): void {
    onMessage(messaging, async (payload) => {
        await defaultCallbackFirebaseMessages(payload);
    });
}

/**
 * Callback di default per la gestione delle notifiche Firebase relative alle chiamate VoIP.
 * @param payload - Il payload della notifica Firebase ricevuta.
 */
export async function defaultCallbackFirebaseMessages(payload: any): Promise<void> {
    logger.info("Notifica Firebase ricevuta:", payload);

    // controlla se l'utente è autenticato
    const currentUserInfo: IMatrixClientCreds | null = await DthSessionStorage.getCredentials();
    if (!currentUserInfo || !currentUserInfo.userId) {
        logger.warn("Notifica Firebase ignorata: utente non autenticato.");
        return;
    }

    const eventType = payload.data?.type?.toUpperCase();
    const reason = payload.data?.reason?.toUpperCase();

    if (reason === "REMOTE_LOCK") {
        // Gestisce il caso di blocco remoto, cioè disconnessione forzata
        logger.warn("Ricevuto evento di blocco remoto. Potrebbe essere necessario disconnettersi o aggiornare lo stato dell'app.");
        Lifecycle.logout();
        return;
    }

    // Accede allo store delle chiamate
    const callState = useCallStore.getState();

    if (payload.data && eventType === "VOIP") {
        const invitationId = payload.data["invitationId"];
        // const type = payload.data["type"];
        const displayName = payload.data["displayName"] || _t("room.call.unknown");

        let room = null;
        let roomId: string | undefined = undefined;
        // Prova a trovare la room associata a quel display name, se presente il matrix client
        const matrixClient = MatrixClientPeg.get();
        if (matrixClient) {
            // Prova a trovare l'ID della room associata a quel display name, 
            // confrontando sia "Nome Cognome" che "Cognome Nome"
            room = findRoomByDisplayName(matrixClient, displayName);
            roomId = room?.roomId;
        }

        switch (reason) {
            case "CALL_INVITATION":
                // Chiamata in arrivo: 
                //  se siamo già in chiamata, rifiuta la nuova chiamata, 
                //  altrimenti mostra la notifica di chiamata in arrivo

                if (callState.inCall) {
                    // Se è già in chiamata, ignora la nuova chiamata in arrivo

                    logger.info("Chiamata in arrivo ignorata perché l'utente è già in chiamata.");
                    await callState.rejectCall(invitationId, displayName, CallRejectReason.refused, roomId, true);
                } else {
                    // Se non è in chiamata, mostra la notifica di nuova chiamata in arrivo

                    // Mostra la notifica di chiamata in arrivo
                    callState.triggerIncomingCall(invitationId, displayName, roomId);
                }
                break;
            case "CALL_CANCELLED":
                // Chiamata annullata: se la chiamata in arrivo corrisponde a quella annullata, chiudi la notifica
                if (callState.isIncomingCall && callState.callStatus === CallStatus.incomingRinging && callState.invitationId === invitationId) {
                    logger.info("Chiamata annullata. Chiudo la notifica di chiamata in arrivo.");
                    await callState.rejectCall(invitationId, displayName, CallRejectReason.missed, roomId, true);
                }
                // Se la chiamata è 1-1 ed è stata cancellata, esco dalla chiamata 
                else if (callState.inCall && !callState.isGroupCall && callState.invitationId === invitationId) {
                    logger.info("Chiamata 1-1 annullata. Esco dalla chiamata.");
                    await callState.leaveCall(CallLeaveReason.exited);
                }

                break;
            case "CALL_ANSWERED_ELSEWHERE":
                // Chiamata risposta su un altro dispositivo: 
                //  se la chiamata in arrivo corrisponde a quella risposta altrove, chiudi la notifica
                if (callState.isIncomingCall && callState.callStatus === CallStatus.incomingRinging && callState.invitationId === invitationId) {
                    logger.info("Chiamata risposta su un altro dispositivo. Chiudo la notifica di chiamata in arrivo.");
                    await callState.rejectCall(invitationId, displayName, CallRejectReason.refused, roomId, true);
                }
                break;
            case "CALL_REJECTED":
                // La persona chiamata ha rifiutato la chiamata

                // se è una chiamata 1:1 e sono ancora in attesa di risposta, chiudi la chiamata
                if (callState.inCall && callState.callStatus !== CallStatus.inCall && !callState.isGroupCall) {
                    logger.info("Chiamata rifiutata. Chiudo la notifica di chiamata in arrivo.");
                    await callState.leaveCall(CallLeaveReason.refused);
                }
                break;
            default:
                logger.warn("Unknown call notification reason:", reason);
        }
    }
}

// /**
//  * Registra un callback per ricevere notifiche push quando l'app è in foreground.
//  *
//  * @param {(payload: any) => void} callback - Funzione chiamata ogni volta che arriva una notifica push.
//  *
//  * @example
//  * listenForFirebaseMessages((payload) => {
//  *   // Mostra una notifica custom, aggiorna lo stato, ecc.
//  *   alert('Nuova notifica: ' + payload.notification?.title);
//  * });
//  */
// export function listenForFirebaseMessages(callback: (payload: any) => void): void {
//     onMessage(messaging, (payload) => {
//         console.log("[Firebase] Notifica Firebase ricevuta:", payload);
//         callback(payload);
//     });
// }
